// File: /var/www/MissionaryIndia/app1/frontend/src/components/SupportRequest.js

import React, { useState, useEffect } from "react";
import axios from "axios";
import "../styles/SupportRequest.css";

function SupportRequest({ user }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    type: "other"
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get("/api/support-requests");
      setRequests(response.data.requests);
    } catch (error) {
      console.error("Failed to fetch support requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.description.trim()) {
      alert("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post("/api/support-requests", formData);
      alert("Support request submitted successfully!");
      setFormData({ subject: "", description: "", type: "other" });
      setShowForm(false);
      fetchRequests(); // Refresh the list
    } catch (error) {
      alert("Failed to submit support request: " + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      open: "status-open",
      in_progress: "status-in-progress",
      completed: "status-completed",
      closed: "status-closed"
    };
    return (
      <span className={`status-badge ${statusColors[status]}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getTypeLabel = (type) => {
    const types = {
      bug: "Bug Report",
      feature: "Feature Request",
      change: "Change Request",
      other: "Other"
    };
    return types[type] || "Other";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="support-request-container">
      <div className="support-header">
        <h2>Support Requests</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="new-request-btn"
        >
          {showForm ? "Cancel" : "+ New Support Request"}
        </button>
      </div>

      {showForm && (
        <div className="support-form-section">
          <h3>Submit New Support Request</h3>
          <form onSubmit={handleSubmit} className="support-form">
            <div className="form-group">
              <label htmlFor="type">Request Type:</label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="form-control"
              >
                <option value="bug">Bug Report</option>
                <option value="feature">Feature Request</option>
                <option value="change">Change Request</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject: *</label>
              <input
                type="text"
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Brief summary of your request..."
                className="form-control"
                required
                maxLength={500}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description: *</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Please provide detailed information about your issue or request..."
                className="form-control"
                rows="8"
                required
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn" disabled={submitting}>
                {submitting ? "Submitting..." : "Submit Request"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="requests-list">
        <h3>My Support Requests ({requests.length})</h3>

        {requests.length === 0 ? (
          <div className="no-requests">
            <p>You haven't submitted any support requests yet.</p>
            <p>Click "New Support Request" above to submit one.</p>
          </div>
        ) : (
          <div className="requests-grid">
            {requests.map((request) => (
              <div key={request.id} className="request-card">
                <div className="request-header">
                  <div className="request-title-row">
                    <h4>{request.subject}</h4>
                    {getStatusBadge(request.status)}
                  </div>
                  <div className="request-meta">
                    <span className="request-type">{getTypeLabel(request.type)}</span>
                    {request.priority && request.priority !== 'normal' && (
                      <span className={`priority-badge priority-${request.priority}`}>
                        {request.priority.toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="request-body">
                  <div className="request-description">
                    {request.description}
                  </div>
                </div>

                {request.admin_response && (
                  <div className="admin-response">
                    <div className="response-header">
                      <strong>Admin Response:</strong>
                      {request.responder_name && (
                        <span className="responder"> by {request.responder_name}</span>
                      )}
                    </div>
                    <div className="response-text">{request.admin_response}</div>
                    {request.responded_at && (
                      <div className="response-date">
                        Responded: {formatDate(request.responded_at)}
                      </div>
                    )}
                  </div>
                )}

                <div className="request-footer">
                  <div className="request-dates">
                    <div>
                      <strong>Submitted:</strong> {formatDate(request.created_at)}
                    </div>
                    {request.completed_at && (
                      <div>
                        <strong>Completed:</strong> {formatDate(request.completed_at)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SupportRequest;
